// ============================================
// Wizard Event Handlers
// 이벤트 위임 패턴으로 모든 마법사 이벤트 처리
// ============================================

import {
  setGender,
  setDescription,
  setIntroScene,
  setImageModelId,
  toggleTag,
  addCustomTag,
  removeCustomTag,
  getWizardState,
  getCurrentStep
} from './state.js';

import {
  goToNextStep,
  goToPreviousStep,
  closeWizardModal,
  selectCreationMethod,
  renderCurrentStep,
  updateNavigationButtons,
  closeCreationMethodModal
} from './modal.js';

import { renderImageModelCarousel } from './steps.js';
import { useCreatorStore } from '../../../stores/creatorStore.js';

// 이미지 모델 캐시
let imageModelsCache = null;

// 이미지 모델 캐시 getter (외부에서 사용)
export function getImageModelsCache() {
  return imageModelsCache;
}

// 중복 바인딩 방지 플래그
let wizardEventsBound = false;

// ============================================
// 이벤트 바인딩
// ============================================

export function bindWizardEvents() {
  // 이미 바인딩되었으면 무시
  if (wizardEventsBound) return;

  // 선택 모달 이벤트
  const methodModal = document.getElementById('creationMethodModal');
  if (methodModal) {
    methodModal.addEventListener('click', handleMethodModalClick);
  }

  // 마법사 모달 이벤트
  const wizardModal = document.getElementById('aiWizardModal');
  if (wizardModal) {
    wizardModal.addEventListener('click', handleWizardClick);
    wizardModal.addEventListener('input', handleWizardInput);
    wizardModal.addEventListener('change', handleWizardChange);
    wizardModal.addEventListener('keydown', handleWizardKeydown);
  }

  wizardEventsBound = true;
}

export function unbindWizardEvents() {
  const methodModal = document.getElementById('creationMethodModal');
  if (methodModal) {
    methodModal.removeEventListener('click', handleMethodModalClick);
  }

  const wizardModal = document.getElementById('aiWizardModal');
  if (wizardModal) {
    wizardModal.removeEventListener('click', handleWizardClick);
    wizardModal.removeEventListener('input', handleWizardInput);
    wizardModal.removeEventListener('change', handleWizardChange);
    wizardModal.removeEventListener('keydown', handleWizardKeydown);
  }

  wizardEventsBound = false;
}

// ============================================
// 선택 모달 클릭 핸들러
// ============================================

function handleMethodModalClick(e) {
  const actionEl = e.target.closest('[data-action]');

  if (!actionEl) {
    // 배경 클릭 시 닫기
    if (e.target.classList.contains('wizard-method-modal')) {
      closeCreationMethodModal();
    }
    return;
  }

  const action = actionEl.dataset.action;

  switch (action) {
    case 'selectMethod':
      const method = actionEl.dataset.method;
      selectCreationMethod(method);
      break;

    case 'closeMethodModal':
      closeCreationMethodModal();
      break;
  }
}

// ============================================
// 마법사 클릭 핸들러
// ============================================

function handleWizardClick(e) {
  const actionEl = e.target.closest('[data-action]');
  if (!actionEl) {
    // 배경 클릭 시 닫기
    if (e.target.classList.contains('wizard-fullscreen')) {
      closeWizardModal();
    }
    return;
  }

  const action = actionEl.dataset.action;

  switch (action) {
    // 네비게이션
    case 'wizardBack':
      goToPreviousStep();
      break;

    case 'wizardNext':
      goToNextStep();
      break;

    case 'closeWizard':
      closeWizardModal();
      break;

    // Step 1: 성별 선택
    case 'selectGender':
      handleSelectGender(actionEl.dataset.value);
      break;

    // Step 3, 4, 5: 태그
    case 'toggleTag':
      handleToggleTag(actionEl.dataset.tag, actionEl.dataset.tagType);
      break;

    case 'addCustomTag':
      handleAddCustomTag(actionEl.dataset.tagType);
      break;

    case 'removeCustomTag':
      handleRemoveCustomTag(actionEl.dataset.tag, actionEl.dataset.tagType);
      break;

    // Step 7: 이미지 모델 선택
    case 'selectImageModel':
      handleSelectImageModel(actionEl.dataset.modelId);
      break;
  }
}

// ============================================
// 마법사 입력 핸들러
// ============================================

function handleWizardInput(e) {
  const action = e.target.dataset.action;

  switch (action) {
    case 'updateDescription':
      setDescription(e.target.value);
      break;

    case 'updateIntroScene':
      setIntroScene(e.target.value);
      break;
  }
}

// ============================================
// 마법사 체인지 핸들러
// ============================================

function handleWizardChange(e) {
  // 현재 체인지 이벤트 핸들러 없음
}

// ============================================
// 키보드 핸들러
// ============================================

function handleWizardKeydown(e) {
  // 커스텀 태그 입력에서 엔터 키
  const isTagInput = e.target.id === 'wizardCustomTagInput' ||
                     e.target.id === 'wizardCustomFortuneTypeInput' ||
                     e.target.id === 'wizardCustomSpecialtyInput';

  if (isTagInput && e.key === 'Enter') {
    e.preventDefault();
    const tagType = e.target.dataset.tagType || 'style';
    handleAddCustomTag(tagType);
  }
}

// ============================================
// 액션 핸들러
// ============================================

function handleSelectGender(gender) {
  setGender(gender);

  // 전체 리렌더링 대신 클래스만 토글 (번쩍임 방지)
  const cards = document.querySelectorAll('.wizard-gender-card');
  cards.forEach(card => {
    const value = card.dataset.value;
    card.classList.toggle('selected', value === gender);
  });
}

function handleToggleTag(tag, tagType = 'style') {
  toggleTag(tag, tagType);

  // 전체 리렌더링 대신 클래스만 토글 (번쩍임 방지)
  const chips = document.querySelectorAll(`.wizard-tag-chip[data-tag-type="${tagType}"]`);
  chips.forEach(chip => {
    if (chip.dataset.tag === tag) {
      chip.classList.toggle('selected');
    }
  });
}

function handleAddCustomTag(tagType = 'style') {
  let inputId;
  switch (tagType) {
    case 'fortuneType':
      inputId = 'wizardCustomFortuneTypeInput';
      break;
    case 'specialty':
      inputId = 'wizardCustomSpecialtyInput';
      break;
    default:
      inputId = 'wizardCustomTagInput';
  }

  const input = document.getElementById(inputId);
  if (!input) return;

  const tag = input.value.trim();
  if (tag && addCustomTag(tag, tagType)) {
    input.value = '';
    renderCurrentStep();
    // 입력창에 포커스 유지
    setTimeout(() => {
      const newInput = document.getElementById(inputId);
      if (newInput) newInput.focus();
    }, 0);
  }
}

function handleRemoveCustomTag(tag, tagType = 'style') {
  removeCustomTag(tag, tagType);
  renderCurrentStep();
}

function handleSelectImageModel(modelId) {
  setImageModelId(modelId);

  // 선택 UI 업데이트
  const cards = document.querySelectorAll('.wizard-model-card');
  cards.forEach(card => {
    card.classList.toggle('selected', card.dataset.modelId === modelId);
  });

  // 네비게이션 버튼 업데이트 (카본 비용 표시)
  updateNavigationButtons();
}

// ============================================
// 이미지 모델 로드
// ============================================

export async function loadImageModels() {
  // 캐시 확인
  if (imageModelsCache) {
    return imageModelsCache;
  }

  // creatorStore에서 캐시 확인
  const store = useCreatorStore.getState();
  if (store.cache?.imageModels?.length > 0) {
    imageModelsCache = store.cache.imageModels;
    return imageModelsCache;
  }

  // Firestore에서 로드
  try {
    const db = firebase.firestore();
    const snapshot = await db.collection('imageModels').get();

    const models = snapshot.docs
      .filter(doc => doc.data().isPublic === true)
      .map(doc => ({
        id: doc.id,
        name: doc.data().name,
        image: doc.data().image,
        order: doc.data().order || 999,
        carbonCost: doc.data().carbonCost || 3
      }))
      .sort((a, b) => a.order - b.order);

    imageModelsCache = models;

    // 스토어에 캐시
    useCreatorStore.getState().setCache({ imageModels: models });

    return models;
  } catch (error) {
    console.error('[Wizard] 이미지 모델 로드 실패:', error);
    return [];
  }
}

// Step 7 진입 시 호출
export async function loadAndRenderImageModels() {
  const models = await loadImageModels();
  const state = getWizardState();

  // 현재 상태 기준으로 렌더링 (기본값은 'none')
  renderImageModelCarousel(models, state.imageModelId);
  updateNavigationButtons();
}

// ============================================
// 단계 진입 시 초기화
// ============================================

export function onStepEnter(step) {
  // Step 7: 이미지 스타일 선택
  if (step === 7) {
    loadAndRenderImageModels();
  }
}
